import { useEffect, useState } from 'react'
import { history } from 'store'
import { tabFromHash } from './tabFromHash'

// Keeps a page's active tab in step with the URL hash.
//
// Every report page that selects a tab from the hash needs the same three
// things, and getting any of them wrong is a real bug we have already shipped
// once each:
//
//   - resolve the hash EXACTLY, not with `hash.includes(id)` — a substring scan
//     matches any hash that merely mentions a tab id, and lets a shorter id
//     shadow a longer one it prefixes;
//   - seed from the hash on mount, so a deep link opens on its own tab;
//   - keep listening. A same-path hash change (browser Back/Forward, or a nav
//     entry that links to `#tab` on the page you are already on) re-renders
//     without remounting, so a `useState` initializer never runs again. This
//     used to be masked by /reports going through an inline
//     `authenticatedRoute(Reports)`, which remounted the page on every
//     navigation; hoisting that (Routes.tsx) is what exposed it.
//
// `tabs` may change between renders — a flag resolving can widen the visible
// set — so the hash is re-resolved whenever it does, which also means a page
// whose flag arrives late lands on the right tab without needing its own
// effect. Pass a module-level array where the set is fixed; the dependency is
// the contents, not the array identity.
// Gotcha for callers: this follows EVERY history event, and history 4.x's
// object form only inherits `pathname` — `history.push({ pathname })` or
// `history.replace({ search })` reset `hash` to ''. That reads here as "the
// hash went away", so the tab silently snaps back to `fallback`. Any
// navigation on a page using this hook should spread the current location
// (`{ ...history.location, ... }`) rather than pass a bare object.
export const useHashTab = <T extends string>(
  tabs: readonly T[],
  fallback: T
): [T, (tab: T) => void] => {
  const [activeTab, setActiveTab] = useState<T>(() =>
    tabFromHash(history.location.hash, tabs, fallback)
  )

  const tabsKey = tabs.join('\u0000')

  useEffect(() => {
    // Re-resolve in the effect body, not only on the next navigation: when the
    // tab set widens (a flag settling), the hash may now name a tab that was
    // unreachable a render ago.
    setActiveTab(tabFromHash(history.location.hash, tabs, fallback))

    return history.listen(location => {
      setActiveTab(tabFromHash(location.hash, tabs, fallback))
    })
    // `tabs` is compared by contents via tabsKey; using the array itself would
    // resubscribe on every render for callers that build it inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey, fallback])

  return [activeTab, setActiveTab]
}

export default useHashTab
